import { API_PATH } from "@constants/config";
import { request } from "@helper/http";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

import { convertWfToPayloadServer, normalizeWf } from "@/converter/workflow";
import { type APIQuery, type APIResponse } from "@/types/global";
import { type Workflow, type WorkflowPayload } from "@/types/workflow";

export async function getWorkflows(params?: APIQuery): Promise<APIResponse<Workflow>> {
  return request({
    url: `${API_PATH.WORKFLOWS_LIST}/`,
    method: "get",
    params: {
      limit: params?.limit,
      offset: params?.offset,
      search: params?.search,
      ordering: params?.ordering,
    },
  });
}

export const getAllWorkflows = async (limit = 30): Promise<Workflow[]> => {
  let workflows: Workflow[] = [];

  // Function will loop to get all workflows
  const getWorkflowsRecursive = async (offset: number, hasMore: boolean): Promise<void> => {
    if (!hasMore) {
      return;
    }
    const params: APIQuery = { limit: limit, offset };
    const response = await getWorkflows(params);
    hasMore = response.results.length + workflows.length < response.count;
    offset += limit;
    workflows = [...workflows, ...response.results];
    await getWorkflowsRecursive(offset, hasMore);
  };

  // Execute to get all team
  await getWorkflowsRecursive(0, true);
  workflows?.sort((aWorkflow, bWorkflow) => aWorkflow.display_name.localeCompare(bWorkflow.display_name));
  return workflows ?? [];
};

export async function getWorkflowByCodeName(codeName: string): Promise<APIResponse<Workflow>> {
  return request({
    url: `${API_PATH.WORKFLOWS_LIST}/?code_name=${codeName}`,
    method: "get",
  });
}

export const getWorkflowByID = (id: string): Promise<Workflow> => {
  return request({
    url: `${API_PATH.WORKFLOWS_LIST}/${id}/`,
    method: "get",
  })
    .then((res) => normalizeWf(res))
    .catch((error) => {
      return Promise.reject(error);
    });
};

export async function createWorkflow(payload: WorkflowPayload): Promise<Workflow> {
  const data = convertWfToPayloadServer(payload);

  return request({
    url: `${API_PATH.WORKFLOWS_LIST}/`,
    method: "post",
    data,
  });
}

export async function deleteWorkflow(id: string): Promise<APIResponse<Workflow>> {
  return request({
    url: API_PATH.WORKFLOW(id),
    method: "delete",
  });
}

export enum UPDATE_WORKFLOW_PART {
  DETAIL = "detail",
  EXECUTOR = "executor",
  PERMISSION = "permission",
}

export interface UpdateWorkflowRequest {
  id: string;
  part: UPDATE_WORKFLOW_PART;
  payload: Partial<WorkflowPayload>;
}

export async function updateWorkflow({ id, part, payload }: UpdateWorkflowRequest): Promise<APIResponse<Workflow>> {
  return request({
    url: API_PATH.WORKFLOW(`${id}/${part}`),
    method: "patch",
    data: { ...payload },
  });
}

export async function updateWorkflowData(workflowId: string, payload: WorkflowPayload): Promise<Workflow> {
  const data = convertWfToPayloadServer(payload);
  const result = await request({
    url: API_PATH.WORKFLOW(`${workflowId}`),
    method: "patch",
    data,
  });

  return normalizeWf(result);
}

export const workflowApi = createApi({
  reducerPath: "workflowApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Workflows", "WorkflowDetail"],
  endpoints: (build) => ({
    getWorkflows: build.query({
      queryFn: async (params: APIQuery) => {
        try {
          const data: APIResponse<Workflow> = await request({
            url: `${API_PATH.WORKFLOWS_LIST}/`,
            method: "GET",
            params,
          });
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["Workflows"],
    }),
    getWorkflowByID: build.query<Workflow, { id?: string }>({
      queryFn: async ({ id }: { id?: string }) => {
        try {
          if (!id) throw new Error("ID is required");
          const data = await getWorkflowByID(id);

          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["WorkflowDetail"],
    }),
    invalidateWorkflows: build.mutation<null, void>({
      queryFn: () => ({ data: null }),
      invalidatesTags: ["Workflows", "WorkflowDetail"],
    }),
  }),
});

export const {
  useLazyGetWorkflowsQuery,
  useGetWorkflowsQuery,
  useInvalidateWorkflowsMutation,
  useGetWorkflowByIDQuery,
} = workflowApi;
